import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Typography,
  Button,
  TablePagination,
} from '@material-ui/core';
import TableContainer from '@material-ui/core/TableContainer';
import DeleteIcon from '@material-ui/icons/Delete';
import NoteIcon from '@material-ui/icons/Note';
import GetAppIcon from '@material-ui/icons/GetApp';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import TextField from '@material-ui/core/TextField';
import Chip from '@material-ui/core/Chip';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import Checkbox from '@material-ui/core/Checkbox';
import ListItemText from '@material-ui/core/ListItemText';
import DateFnsUtils from '@date-io/date-fns';
import {
  MuiPickersUtilsProvider,
  KeyboardDatePicker,
} from '@material-ui/pickers';
import { pt } from 'date-fns/locale';
import { toast } from 'react-toastify';
import api from '../../../services/api';
import { headerAdd } from '../../../store/modules/header/actions';
import { boxesSetPending } from '../../../store/modules/boxes/actions';
import { ApplicationState } from '../../../store';
import { useStyles } from './TradesHistoryStyles';
import {
  dateToTimezone,
  formatPrice,
  correctInstrument,
  setPriceColor,
} from '../../../utils/functions';

export default function TradesHistory() {
  interface Trade {
    _id: string;
    startDate: string;
    rawStartDate: string;
    endDate: string;
    asset: string;
    instrument: string;
    investment: number;
    result: number;
    formattedInvestment: number;
    formattedResult: number;
    color: string;
    notes: string;
    tags: string[];
  }

  const [trades, setTrades] = useState<Array<Trade>>([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [dateFrom, setDateFrom] = useState<Date | null>(null);
  const [dateTo, setDateTo] = useState<Date | null>(null);
  const [tagFilter, setTagFilter] = useState<string[]>([]);
  const [tagFilterMode, setTagFilterMode] = useState<'any' | 'all'>('any');
  const rowsPerPage = 20;

  const [notesDialogOpen, setNotesDialogOpen] = useState(false);
  const [notesDialogTrade, setNotesDialogTrade] = useState<Trade | null>(null);
  const [notesDialogValue, setNotesDialogValue] = useState('');
  const [dialogTags, setDialogTags] = useState<string[]>([]);

  const userState = useSelector((state: ApplicationState) => state.user);

  function handleOpenNotes(trade: Trade) {
    setNotesDialogTrade(trade);
    setNotesDialogValue(trade.notes);
    setDialogTags(trade.tags || []);
    setNotesDialogOpen(true);
  }

  function handleSaveNotes() {
    if (!notesDialogTrade) return;
    api.put('tradeHistory', {
      _id: notesDialogTrade._id,
      notes: notesDialogValue,
      tags: dialogTags,
    }).then(() => {
      setTrades(prev => prev.map(t =>
        t._id === notesDialogTrade._id ? { ...t, notes: notesDialogValue, tags: dialogTags } : t
      ));
      setNotesDialogOpen(false);
      toast.success('Notas salvas com sucesso!');
    }).catch(err => {
      toast.error((err.response && err.response.data && err.response.data.error) || 'Erro ao salvar notas');
    });
  }

  useEffect(() => {
    api.get('/tradeHistory').then(res => {
      const { data } = res;
      const parsedData = data.map((item: any) => {
        return {
          ...item,
          rawStartDate: item.startDate,
          startDate: dateToTimezone(item.startDate),
          endDate: dateToTimezone(item.endDate),
          instrument: correctInstrument(item.instrument),
          formattedInvestment: formatPrice(item.investment),
          formattedResult: formatPrice(item.result),
          color: setPriceColor(item.result),
          notes: item.notes || '',
          tags: item.tags || [],
        };
      });

      setTrades(parsedData);
      setInitialLoading(false);
    }).catch(() => {
      setInitialLoading(false);
    });
  }, []);

  const dispatch = useDispatch();

  function handleDelete(trade: Trade) {
    api
      .delete('tradeHistory', {
        data: {
          _id: trade._id,
        },
      })
      .then(res => {
        toast.success(res.data.message);

        setTrades(trades.filter(a => a._id !== trade._id));

        dispatch(boxesSetPending());
        dispatch(headerAdd(trade.result * -1));
      })
      .catch(err => {
        toast.error(err.response.data.error);
      });
  }

  function handleClearFilter() {
    setDateFrom(null);
    setDateTo(null);
    setTagFilter([]);
    setTagFilterMode('any');
    setPage(0);
  }

  function handleExport() {
    const params: any = {};
    if (dateFrom) params.dateFrom = dateFrom.toISOString();
    if (dateTo) params.dateTo = dateTo.toISOString();
    if (tagFilter.length > 0) {
      params.tags = tagFilter.join(',');
      params.tagMode = tagFilterMode;
    }
    api.get('/tradeHistory/export', { responseType: 'blob', params }).then(res => {
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'trades-dashtrader.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    }).catch(() => {
      toast.error('Erro ao exportar trades');
    });
  }

  const filteredTrades = trades.filter(trade => {
    if (tagFilter.length > 0) {
      if (!trade.tags || trade.tags.length === 0) return false;
      if (tagFilterMode === 'any') {
        if (!tagFilter.some(t => trade.tags.includes(t))) return false;
      } else {
        if (!tagFilter.every(t => trade.tags.includes(t))) return false;
      }
    }
    if (!dateFrom && !dateTo) return true;
    const tradeDate = new Date(trade.rawStartDate);
    if (dateFrom) {
      const from = new Date(dateFrom);
      from.setHours(0, 0, 0, 0);
      if (tradeDate < from) return false;
    }
    if (dateTo) {
      const to = new Date(dateTo);
      to.setHours(23, 59, 59, 999);
      if (tradeDate > to) return false;
    }
    return true;
  });

  const paginatedTrades = filteredTrades.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const classes = useStyles();
  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <Typography className={classes.title}>
          Histórico de trades
        </Typography>
        <Button
          size="small"
          variant="outlined"
          onClick={handleExport}
          startIcon={<GetAppIcon style={{ fontSize: '16px' }} />}
          style={{ color: '#ffffffcc', borderColor: '#ffffff33', textTransform: 'none', fontSize: '13px' }}
        >
          Exportar CSV
        </Button>
      </div>

      <div className={classes.filters}>
        <MuiPickersUtilsProvider utils={DateFnsUtils} locale={pt}>
          <KeyboardDatePicker
            disableToolbar
            variant="inline"
            format="dd/MM/yyyy"
            margin="normal"
            label="De"
            value={dateFrom}
            onChange={(date) => { setDateFrom(date); setPage(0); }}
            KeyboardButtonProps={{ 'aria-label': 'data inicio' }}
            autoOk
            style={{ maxWidth: '150px', margin: 0 }}
          />
          <KeyboardDatePicker
            disableToolbar
            variant="inline"
            format="dd/MM/yyyy"
            margin="normal"
            label="Até"
            value={dateTo}
            onChange={(date) => { setDateTo(date); setPage(0); }}
            KeyboardButtonProps={{ 'aria-label': 'data fim' }}
            autoOk
            style={{ maxWidth: '150px', margin: 0 }}
          />
        </MuiPickersUtilsProvider>
        <FormControl style={{ minWidth: 150 }}>
          <InputLabel id="tag-filter-label" shrink>Tags</InputLabel>
          <Select
            labelId="tag-filter-label"
            multiple
            value={tagFilter}
            onChange={(e: React.ChangeEvent<{ value: unknown }>) => { setTagFilter(e.target.value as string[]); setPage(0); }}
            displayEmpty
            renderValue={(selected) => {
              const sel = selected as string[];
              if (sel.length === 0) return <span style={{ opacity: 0.5 }}>Todas</span>;
              return sel.join(', ');
            }}
            MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
          >
            {(userState.user.config.tags || []).map((tag: string) => (
              <MenuItem key={tag} value={tag}>
                <Checkbox checked={tagFilter.includes(tag)} size="small" color="primary" />
                <ListItemText primary={tag} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {tagFilter.length > 1 && (
          <FormControl style={{ minWidth: 100 }}>
            <InputLabel id="tag-mode-label" shrink>Modo</InputLabel>
            <Select
              labelId="tag-mode-label"
              value={tagFilterMode}
              onChange={(e: any) => { setTagFilterMode(e.target.value); setPage(0); }}
            >
              <MenuItem value="any">Qualquer</MenuItem>
              <MenuItem value="all">Todas</MenuItem>
            </Select>
          </FormControl>
        )}
        {(dateFrom || dateTo || tagFilter.length > 0) && (
          <Button
            size="small"
            variant="outlined"
            onClick={handleClearFilter}
            style={{ color: '#ffffffcc', borderColor: '#ffffff33', textTransform: 'none' }}
          >
            Limpar
          </Button>
        )}
      </div>

      <TableContainer component={Paper} className={classes.paper}>
        <Table className={classes.table} size="small">
          <TableHead>
            <TableRow>
              <TableCell>Data</TableCell>
              <TableCell align="center">Ativo</TableCell>
              <TableCell align="center">Tipo</TableCell>
              <TableCell align="center">Investimento</TableCell>
              <TableCell align="center">Resultado</TableCell>
              <TableCell align="center">Tags</TableCell>
              <TableCell align="center">Notas</TableCell>
              <TableCell align="right">Remover</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {initialLoading && Array.from({ length: 8 }).map((_, i) => {
              const widths = ['90px', '60px', '80px', '70px', '70px', '50px', '28px', '28px'];
              const aligns: Array<'left' | 'center' | 'right'> = ['left', 'center', 'center', 'center', 'center', 'center', 'center', 'right'];
              return (
                <TableRow key={`skeleton-${i}`}>
                  {widths.map((w, j) => (
                    <TableCell key={j} align={aligns[j]}>
                      <div style={{
                        height: j >= 6 ? '24px' : '14px',
                        width: w,
                        borderRadius: j >= 6 ? '50%' : '4px',
                        background: 'linear-gradient(90deg, #ffffff06 0%, #ffffff10 50%, #ffffff06 100%)',
                        backgroundSize: '400px 100%',
                        animation: 'shimmer 1.5s infinite ease-in-out',
                        display: 'inline-block',
                      }} />
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
            {!initialLoading && paginatedTrades.map(trade => (
              <TableRow className={classes.row} key={trade._id}>
                <TableCell component="th" scope="row">
                  {trade.startDate}
                </TableCell>
                <TableCell align="center">{trade.asset}</TableCell>
                <TableCell align="center">{trade.instrument}</TableCell>
                <TableCell align="center">
                  {trade.formattedInvestment}
                </TableCell>
                <TableCell align="center" style={{ color: trade.color }}>
                  {trade.formattedResult}
                </TableCell>
                <TableCell align="center">
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px', justifyContent: 'center' }}>
                    {trade.tags && trade.tags.map((tag: string) => (
                      <Chip key={tag} label={tag} size="small" style={{ height: '20px', fontSize: '11px', background: '#1E2141' }} />
                    ))}
                  </div>
                </TableCell>
                <TableCell align="center">
                  <IconButton size="small" onClick={() => handleOpenNotes(trade)} style={{ color: trade.notes ? '#ffee58' : '#ffffff33' }}>
                    <NoteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => handleDelete(trade)} style={{ color: '#ffffff44' }}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {!initialLoading && paginatedTrades.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center" style={{ padding: '40px', color: '#ffffff55' }}>
                  Nenhum trade encontrado.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        {!initialLoading && filteredTrades.length > 0 && (
          <TablePagination
            className={classes.pagination}
            component="div"
            count={filteredTrades.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPageOptions={[20]}
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
          />
        )}
      </TableContainer>

      <Dialog
        open={notesDialogOpen}
        onClose={() => setNotesDialogOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          style: {
            background: '#2C325C',
            color: '#fff',
            borderRadius: '12px',
          },
        }}
      >
        <DialogTitle style={{ paddingBottom: '8px' }}>Notas e Tags</DialogTitle>
        <DialogContent>
          {userState.user.config.tags && userState.user.config.tags.length > 0 && (
            <FormControl fullWidth style={{ marginBottom: '16px' }}>
              <InputLabel id="dialog-tags-select-label">Tags</InputLabel>
              <Select
                labelId="dialog-tags-select-label"
                multiple
                value={dialogTags}
                onChange={(e: React.ChangeEvent<{ value: unknown }>) => setDialogTags(e.target.value as string[])}
                renderValue={(selected) => (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                    {(selected as string[]).map((tag) => (
                      <Chip key={tag} label={tag} size="small" style={{ background: '#1E2141' }} />
                    ))}
                  </div>
                )}
                MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
              >
                {userState.user.config.tags.map((tag: string) => (
                  <MenuItem key={tag} value={tag}>
                    <Checkbox checked={dialogTags.includes(tag)} size="small" color="primary" />
                    <ListItemText primary={tag} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <TextField
            autoFocus
            multiline
            rows={4}
            fullWidth
            variant="outlined"
            placeholder="Ex: Estratégia, emoção, contexto do mercado..."
            value={notesDialogValue}
            onChange={(e) => setNotesDialogValue(e.target.value)}
          />
        </DialogContent>
        <DialogActions style={{ padding: '16px 24px' }}>
          <Button onClick={() => setNotesDialogOpen(false)} style={{ color: '#ffffffcc' }}>
            Cancelar
          </Button>
          <Button
            onClick={handleSaveNotes}
            color="primary"
            variant="contained"
            style={{ color: '#1E2141', fontWeight: 700 }}
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
